import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { AuthApi } from './auth.api';
import { AuthStore } from './auth.store';
import { getRoleKey } from './auth.types';
import { TokenRefreshService } from './token-refresh.service';
import { RoleApi } from '../../shared/data-access/role.api';
import { BiometricService, type BiometricType } from './services/biometric.service';
import { tryDecodeJwtPayload } from './jwt';

/**
 * Injectable service orchestrating the logout sequence.
 *
 * Core layer — infrastructure concern.
 *
 * Responsibilities:
 *   - Call POST /auth/logout (fail-open on error)
 *   - Clear local auth state via AuthStore.clearSession()
 *   - Stop proactive token refresh via TokenRefreshService.stopRefresh()
 *   - Redirect to /login
 *
 * Fail-open design: if the API call fails, cleanup still runs.
 * The user is never trapped in a session they cannot close.
 */
@Injectable({ providedIn: 'root' })
export class LogoutService {
  private readonly authApi = inject(AuthApi);
  private readonly authStore = inject(AuthStore);
  private readonly tokenRefreshService = inject(TokenRefreshService);
  private readonly roleApi = inject(RoleApi);
  private readonly router = inject(Router);
  private readonly biometricService = inject(BiometricService);

  private readonly _isLoggingOut = signal(false);
  readonly isLoggingOut = this._isLoggingOut.asReadonly();

  logout(): void {
    this._isLoggingOut.set(true);
    this.authApi
      .logout()
      .pipe(
        finalize(() => {
          // D-04a: scoped vault delete — only active type, other type untouched (staff vs socio coexistence)
          // Fire-and-forget deleteData with catch empty to keep void signature; change-password does NOT delete
          try {
            if (Capacitor.isNativePlatform()) {
              const activeType = this.resolveActiveType();
              const key = this.biometricService.keyFor(activeType);
              void NativeBiometric.deleteData({ key }).catch(() => {});
            }
          } catch {
            // ignore — never block logout finalize
          }
          this.authStore.clearSession();
          this.tokenRefreshService.stopRefresh();
          this.roleApi.invalidateCache();
          this.router.navigate(['/login']);
          this._isLoggingOut.set(false);
        })
      )
      .subscribe({ error: () => {} }); // swallow error — fail-open
  }

  private resolveActiveType(): BiometricType {
    // Prefer explicit lastType (set on enroll/unlock) for accuracy when token/user not yet cleared
    const last = localStorage.getItem('tenso.session.lastType') as BiometricType | null;
    if (last === 'staff' || last === 'socio') return last;
    const user = this.authStore.user();
    if (user) {
      const key = getRoleKey(user);
      if (key === 'socio' || key === 'member') return 'socio';
    }
    const token = this.authStore.accessToken();
    if (token) {
      const payload = tryDecodeJwtPayload<{ subjectType?: string; rol?: string }>(token);
      if (payload) {
        if (payload.subjectType === 'member') return 'socio';
        if (payload.rol === 'socio' || payload.rol === 'member') return 'socio';
      }
    }
    return 'staff';
  }
}
